import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
    AlertController,
    IonBackButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonTitle,
    IonToolbar,
} from '@ionic/angular/standalone';
import { ChartConfigurationProvider } from '../../providers/chart-configuration-provider';

@Component({
    selector: 'app-tab-configurations',
    host: { class: 'ion-page' },
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonBackButton, IonItem, IonLabel, IonButton, IonIcon],
    template: `
        <ion-header>
            <ion-toolbar>
                <ion-buttons slot="start">
                    <ion-back-button defaultHref="/tabs"></ion-back-button>
                </ion-buttons>
                <ion-title>Manage Configurations</ion-title>
            </ion-toolbar>
        </ion-header>

        <ion-content>
            @if (chartConfigurationNames.length) {
                @for (configurationName of chartConfigurationNames; track configurationName) {
                    <ion-item>
                        <div class="label-container">
                            <ion-label class="ion-text-wrap">{{ configurationName }}</ion-label>
                            <ion-button (click)="promptRenameConfiguration(configurationName)" fill="clear" class="rename-button">
                                <ion-icon slot="icon-only" name="pencil-outline"></ion-icon>
                            </ion-button>
                            <ion-button (click)="confirmDeleteChartConfiguration(configurationName)" fill="clear" class="delete-button">
                                <ion-icon slot="icon-only" src="assets/icons/bin.svg"></ion-icon>
                            </ion-button>
                        </div>
                    </ion-item>
                }
            } @else {
                <ion-item>
                    <ion-label>No Saved Configurations</ion-label>
                </ion-item>
            }
        </ion-content>
    `,
    styles: `
        ion-item {
            --highlight-background: transparent !important;

            div.label-container {
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                flex-grow: 1;
                align-items: center;
            }

            ion-button.rename-button {
                --padding-end: 8px;
                --padding-start: 12px;
                margin-left: auto;
            }

            ion-button.delete-button {
                --padding-end: 8px;
                --padding-start: 12px;
                margin-left: 0.5rem;
            }
        }
    `,
})
export class TabConfigurationsPageComponent {
    private readonly alertCtrlr = inject(AlertController);
    private readonly chartConfigProvider = inject(ChartConfigurationProvider);

    get chartConfigurationNames() {
        return this.chartConfigProvider.chartConfigurationNames();
    }

    async confirmDeleteChartConfiguration(key: string) {
        const alert = await this.alertCtrlr.create({
            header: 'Delete Configuration',
            message: `Are you sure you want to delete "${key}"?`,
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel',
                },
                {
                    text: 'Delete',
                    role: 'destructive',
                    handler: () => {
                        this.chartConfigProvider.deleteConfiguration(key);
                    },
                },
            ],
        });
        await alert.present();
    }

    async promptRenameConfiguration(key: string) {
        const alert = await this.alertCtrlr.create({
            header: 'Rename Configuration',
            inputs: [
                {
                    name: 'newName',
                    type: 'text',
                    value: key,
                    placeholder: 'New name',
                },
            ],
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel',
                },
                {
                    text: 'Rename',
                    handler: (data) => {
                        if (data.newName && data.newName.trim().length > 0 && data.newName !== key) {
                            this.chartConfigProvider.renameConfiguration(key, data.newName.trim());
                        }
                    },
                },
            ],
        });
        await alert.present();
    }
}
